import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Send, ShieldCheck, CheckCircle2, AlertTriangle, RefreshCw, History } from "lucide-react";

const fmtDay = (iso) => (iso
  ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" })
  : "—");

const fmtDate = (iso) => (iso
  ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric", timeZone: "Europe/Rome" })
  : "—");

export default function AutoForwardPreview() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["auto-forward-preview"],
    queryFn: async () => (await api.get("/auto-forward/preview")).data,
  });
  const { data: log, isLoading: logLoading } = useQuery({
    queryKey: ["auto-forward-log"],
    queryFn: async () => (await api.get("/auto-forward/log")).data,
  });

  const runNow = useMutation({
    mutationFn: async () => (await api.post("/auto-forward/run")).data,
    onSuccess: (res) => {
      toast.success(`Inoltro eseguito: ${res.sent || 0} inviati, ${res.skipped || 0} saltati`);
      qc.invalidateQueries({ queryKey: ["auto-forward-preview"] });
      qc.invalidateQueries({ queryKey: ["auto-forward-log"] });
    },
    onError: () => toast.error("Errore durante l'inoltro"),
  });

  const enabled = data?.enabled;

  return (
    <div className="space-y-6" data-testid="auto-forward-page">
      <Card className={`border-2 shadow-sm ${enabled ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`}>
        <CardContent className="flex flex-wrap items-center justify-between gap-3 p-5">
          <div>
            <div className={`flex items-center gap-2 font-semibold ${enabled ? "text-emerald-800" : "text-amber-800"}`}>
              <ShieldCheck className="h-5 w-5" />
              {enabled ? "Inoltro automatico ATTIVO" : "Inoltro automatico non attivo"}
            </div>
            <p className={`mt-1 text-sm ${enabled ? "text-emerald-700" : "text-amber-700"}`}>
              I <b>già clienti</b> vengono inoltrati alla casella del commerciale che ha fatto <b>l'ultima vendita</b> con quel cliente.
              L'invio reale parte <b>dalle richieste ricevute dal {fmtDate(data?.start_at)}</b>, prende solo i messaggi <b>non letti</b> su dem@italiacorsi.it e li segna come letti dopo l'invio.
            </p>
          </div>
          <Button
            data-testid="auto-forward-run-btn"
            onClick={() => runNow.mutate()}
            disabled={runNow.isPending}
            className="gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${runNow.isPending ? "animate-spin" : ""}`} /> Esegui inoltro ora
          </Button>
        </CardContent>
      </Card>

      <Tabs defaultValue="preview">
        <TabsList data-testid="auto-forward-tabs">
          <TabsTrigger value="preview" data-testid="tab-preview">Da inoltrare</TabsTrigger>
          <TabsTrigger value="log" data-testid="tab-log">Registro inoltri</TabsTrigger>
        </TabsList>

        <TabsContent value="preview" className="mt-4 space-y-6">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Kpi tone="slate" label="Già clienti (totale)" value={isLoading ? null : data?.count ?? 0} testid="af-total" />
            <Kpi tone="emerald" label="Pronti da inoltrare" value={isLoading ? null : data?.forwardable ?? 0} testid="af-ready" />
            <Kpi tone="sky" label="Già inoltrati" value={isLoading ? null : data?.sent_count ?? 0} testid="af-sent" />
            <Kpi tone="rose" label="Senza casella proprietario" value={isLoading ? null : data?.missing_mailbox ?? 0} testid="af-missing" />
          </div>

          <Card className="overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-secondary/60">
                    {["Ricevuto", "Cliente", "Acquisti precedenti", "Corso / Campagna", "Inoltro a (ultima vendita)", "Casella", "Stato"].map((h) => (
                      <TableHead key={h} className="text-xs font-semibold uppercase tracking-wider">{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    Array.from({ length: 6 }).map((_, i) => (
                      <TableRow key={i}>{Array.from({ length: 7 }).map((__, j) => <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>)}</TableRow>
                    ))
                  ) : data?.items?.length ? (
                    data.items.map((r) => (
                      <TableRow key={r.lead_id} data-testid="af-row" className="cursor-pointer hover:bg-secondary/40" onClick={() => navigate(`/leads/${r.lead_id}`)}>
                        <TableCell className="whitespace-nowrap text-sm text-muted-foreground">{fmtDay(r.received_at)}</TableCell>
                        <TableCell className="text-sm">
                          <div className="font-medium">{r.name || "—"}</div>
                          <div className="text-xs text-muted-foreground">{r.email}{r.phone ? ` · ${r.phone}` : ""}</div>
                        </TableCell>
                        <TableCell className="text-sm" data-testid="af-purchases">
                          {r.purchases?.length ? (
                            <div className="space-y-0.5">
                              {r.purchases.slice(0, 3).map((p, i) => (
                                <div key={i} className="text-xs">
                                  <span className="font-medium">{p.title || "Affare vinto"}</span>
                                  <span className="text-emerald-700"> · € {Math.round(p.value).toLocaleString("it-IT")}</span>
                                  <span className="text-muted-foreground"> · {fmtDay(p.won_at)}</span>
                                </div>
                              ))}
                              {r.purchases.length > 3 && <div className="text-[11px] text-muted-foreground">+{r.purchases.length - 3} altri · tot € {Math.round(r.purchases_total).toLocaleString("it-IT")}</div>}
                            </div>
                          ) : <span className="text-xs text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="text-sm">
                          <div>{r.course || "—"}</div>
                          <div className="text-xs text-muted-foreground">{r.campaign}{r.channel ? ` · ${r.channel}` : ""}</div>
                        </TableCell>
                        <TableCell className="text-sm font-medium">{r.owner || "—"}</TableCell>
                        <TableCell className="text-sm">{r.dest_mailbox || <span className="text-rose-600">nessuna casella</span>}</TableCell>
                        <TableCell>
                          {r.forwarded ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2 py-0.5 text-xs font-semibold text-sky-700" title={r.forwarded_at ? `Inoltrato il ${fmtDay(r.forwarded_at)}` : ""}><Send className="h-3 w-3" /> Inoltrato</span>
                          ) : r.can_forward ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700"><CheckCircle2 className="h-3 w-3" /> Pronto</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700" title={r.gmail_message_id ? "Manca casella proprietario" : "Manca email originale"}><AlertTriangle className="h-3 w-3" /> {r.dest_mailbox ? "No email orig." : "No casella"}</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} className="py-16 text-center text-sm text-muted-foreground">Nessun già cliente da inoltrare.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="log" className="mt-4 space-y-6">
          <div className="grid grid-cols-3 gap-4">
            <Kpi tone="slate" label="Totale registrati" value={logLoading ? null : log?.count ?? 0} testid="log-total" />
            <Kpi tone="sky" label="Inviati dal sistema" value={logLoading ? null : log?.sent ?? 0} testid="log-sent" />
            <Kpi tone="amber" label="Saltati (già letti)" value={logLoading ? null : log?.skipped ?? 0} testid="log-skipped" />
          </div>

          <Card className="overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-secondary/60">
                    {["Data inoltro", "Cliente", "Corso", "Commerciale", "Casella", "Esito"].map((h) => (
                      <TableHead key={h} className="text-xs font-semibold uppercase tracking-wider">{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logLoading ? (
                    Array.from({ length: 6 }).map((_, i) => (
                      <TableRow key={i}>{Array.from({ length: 6 }).map((__, j) => <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>)}</TableRow>
                    ))
                  ) : log?.items?.length ? (
                    log.items.map((r) => (
                      <TableRow key={r.lead_id} data-testid="log-row" className="cursor-pointer hover:bg-secondary/40" onClick={() => navigate(`/leads/${r.lead_id}`)}>
                        <TableCell className="whitespace-nowrap text-sm text-muted-foreground">{fmtDay(r.at)}</TableCell>
                        <TableCell className="text-sm">
                          <div className="font-medium">{r.name || "—"}</div>
                          <div className="text-xs text-muted-foreground">{r.email}</div>
                        </TableCell>
                        <TableCell className="text-sm">{r.course || "—"}</TableCell>
                        <TableCell className="text-sm font-medium">{r.to || "—"}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{r.mailbox || "—"}</TableCell>
                        <TableCell>
                          {r.sent ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2 py-0.5 text-xs font-semibold text-sky-700"><Send className="h-3 w-3" /> Inviato</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700" title={r.skipped || ""}><AlertTriangle className="h-3 w-3" /> Saltato{r.skipped ? ` · ${r.skipped}` : ""}</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="py-16 text-center text-sm text-muted-foreground">
                        <History className="mx-auto mb-2 h-6 w-6 opacity-40" />
                        Nessun inoltro automatico registrato finora.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const TONES = {
  slate: "bg-slate-100 text-slate-700",
  emerald: "bg-emerald-100 text-emerald-700",
  sky: "bg-sky-100 text-sky-700",
  amber: "bg-amber-100 text-amber-700",
  rose: "bg-rose-100 text-rose-700",
};

function Kpi({ label, value, tone, testid }) {
  return (
    <Card className="p-4 shadow-sm" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${TONES[tone]}`}>
          <Send className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
          {value == null ? <Skeleton className="mt-1 h-6 w-12" /> : <p className="text-2xl font-bold">{value}</p>}
        </div>
      </div>
    </Card>
  );
}
